import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { client, APIError } from "@/api"
import type { AdminWelcomeOfferDetail } from "@/hooks/useUsers"

export interface UserFeatureFlag {
  key: string
  name: string
  description: string | null
  evaluationLevel: string
  defaultValue: boolean
  enabled: boolean
  source: string
  teamOverride: boolean | null
}

interface UserTeam {
  public_id: string
  name: string
  role: string
}

interface UserStats {
  teamCount: number
  gameCount: number
  clipCount: number
  diagramCount: number
}

interface UserDetail {
  user: {
    public_id: string
    name: string
    email: string
    email_verified: boolean
    is_superuser: boolean
    suspended_at: string | null
    suspension_reason: string | null
    suspension_expires_at: string | null
    created_at: string
    signup_auth_provider: string | null
    last_login_auth_provider: string | null
    last_login: string | null
    signup_surface?: string | null
    signup_source?: {
      referrer_host?: string
      utm_source?: string
      utm_medium?: string
      utm_campaign?: string
      landing?: string
    } | null
    /** Whether a Google Ads click id is stored. The id itself is never sent. */
    has_google_ads_click?: boolean
  }
  teams: UserTeam[]
  stats: UserStats
  welcome_offer: AdminWelcomeOfferDetail | null
  referring_affiliate: { public_id: string; name: string } | null
  claimed_count: number | null
  cap: number
}

export function useUserDetail(userId: string) {
  return useQuery<UserDetail>({
    queryKey: ["user", userId],
    queryFn: async () => {
      const response = await client.admin.users[":publicId"].$get({
        param: { publicId: userId },
      })

      if (!response.ok) {
        throw new Error("Failed to fetch user details")
      }

      return response.json()
    },
    enabled: !!userId,
  })
}

// Manual attribution: the backend only fills an empty attribution (409 once one is set).
export function useAttributeUserToAffiliate() {
  const queryClient = useQueryClient()
  return useMutation<
    { referring_affiliate: { public_id: string; name: string } },
    APIError,
    { userId: string; affiliatePublicId: string }
  >({
    mutationFn: async ({ userId, affiliatePublicId }) => {
      const response = await client.admin.users[":publicId"]["referring-affiliate"].$patch({
        param: { publicId: userId },
        json: { affiliatePublicId },
      })
      if (!response.ok) {
        const err = (await response.clone().json().catch(() => ({}))) as {
          code?: string
          message?: string
        }
        throw new APIError(
          response.status,
          err.code || "USER_ATTRIBUTE_AFFILIATE_FAILED",
          new Error(err.message || "Failed to attribute the user to the affiliate")
        )
      }
      return response.json()
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user", variables.userId] })
      queryClient.invalidateQueries({ queryKey: ["affiliate"] })
      queryClient.invalidateQueries({ queryKey: ["affiliates"] })
    },
  })
}

export function useSuspendUser() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ userId, reason, expiresAt }: { userId: string; reason: string; expiresAt?: string }) => {
      const response = await client.admin.users[":publicId"].suspend.$post({
        param: { publicId: userId },
        json: { reason, expiresAt },
      })
      
      if (!response.ok) {
        throw new Error("Failed to suspend user")
      }
      
      return response.json()
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user", variables.userId] })
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] })
    },
  })
}

export function useUnsuspendUser() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const response = await client.admin.users[":publicId"].unsuspend.$post({
        param: { publicId: userId },
      })
      
      if (!response.ok) {
        throw new Error("Failed to unsuspend user")
      }
      
      return response.json()
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user", variables.userId] })
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] })
    },
  })
}

export function useRevokeUserSessions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const response = await client.admin.users[":publicId"]["revoke-sessions"].$post({
        param: { publicId: userId },
      })

      if (!response.ok) {
        throw new Error("Failed to revoke user sessions")
      }

      return response.json()
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user", variables.userId] })
    },
  })
}

export function useUserFeatureFlags(userId: string) {
  return useQuery<{ userId: string; flags: UserFeatureFlag[] }>({
    queryKey: ["user", userId, "feature-flags"],
    queryFn: async () => {
      const response = await (client.admin.users[":publicId"]["feature-flags"] as any).$get({
        param: { publicId: userId },
      })

      if (!response.ok) {
        throw new Error("Failed to fetch user feature flags")
      }

      return response.json()
    },
    enabled: !!userId,
  })
}

export function useUpdateUserFeatureFlag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      userId,
      flagKey,
      enabled,
    }: {
      userId: string
      flagKey: string
      enabled: boolean
    }) => {
      const response = await (
        client.admin.users[":publicId"]["feature-flags"][":flagKey"] as any
      ).$put({
        param: { publicId: userId, flagKey },
        json: { enabled },
      })

      if (!response.ok) {
        throw new Error("Failed to update feature flag")
      }

      return response.json()
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user", variables.userId, "feature-flags"] })
    },
  })
}